import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import EditIcon from "@mui/icons-material/Edit";
import BoltIcon from "@mui/icons-material/Bolt";

export interface StageAccordionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  status: string;
  statusColor?: "success" | "error" | "warning" | "gapRule" | "neutral";
  defaultExpanded?: boolean;
  children?: React.ReactNode;
}
const statusIcons = {
  success: <CheckIcon />,
  error: <ReportProblemIcon />,
  warning: <EditIcon />,
  gapRule: <BoltIcon />,
  // "n/a" carries no symbol
  neutral: undefined,
};

const statusStyles = {
  success: {
    color: "#1B5E20",
    bgcolor: "#EDF7ED",
    borderColor: "#2E7D3280",
  },
  error: {
    color: "#C62828",
    bgcolor: "#FDEDED",
    borderColor: "#D32F2F80",
  },
  warning: {
    color: "#ED6C02",
    bgcolor: "#FFF4E5",
    borderColor: "#ED6C0280",
  },
  gapRule: {
    color: "#7B1FA2",
    bgcolor: "#F3E5F5",
    borderColor: "#9C27B080",
  },
  neutral: {
    color: "#00000099",
    bgcolor: "#F5F5F5",
    borderColor: "#BDBDBD",
  },
};

export default function LineageAccordionList({
  title,
  subtitle,
  icon,
  status,
  statusColor = "success",
  defaultExpanded = false,
  children,
}: StageAccordionProps) {
  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      elevation={0}
      sx={{
        mb: 1,
        border: "1px solid #BDBDBD",
        borderRadius: "5px !important",
        overflow: "hidden",
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: "#00000061",
            }}
          />
        }
        sx={{
          px: 1,
          "& .MuiAccordionSummary-content": {
            margin: "0",
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center">
            <Box
              mr={1}
              sx={{
                color: "#9E9E9E",
                display: "flex",
                alignItems: "center",
                fontSize: "20px",
              }}
            >
              {icon}
            </Box>

            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: 13,
                  lineHeight: "22px",
                  letterSpacing: 0.5,
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  color: "#00000099",
                  fontSize: 8,
                  fontStyle: "italic",
                }}
              >
                {subtitle}
              </Typography>
            </Box>
          </Box>

          <Chip
            label={status}
            icon={statusIcons[statusColor]}
            variant="outlined"
            sx={{
              borderRadius: "4px",
              height: 20,
              mr: 1,
              fontSize: 10,
              fontWeight: 500,
              py: 0.5,
              px: "0px !important",
              ...statusStyles[statusColor],
              "& .MuiChip-label": {
                px: 0.5,
              },
              "& .MuiChip-icon": {
                fontSize: 14,
                marginLeft: "4px",
                marginRight: "-2px",
                color: "inherit",
              },
            }}
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          borderTop: "1px solid #BDBDBD",
          bgcolor: "#FFFFFF",
          p: 2,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
